import type { ComponentProps, ReactNode } from "react";
import { Link } from "react-router-dom";

import { ColocaNavMain, type ColocaNavMainSection } from "@/shared/components/layout/ColocaNavMain";
import {
  ColocaNavSecondary,
  type ColocaNavSecondaryItem,
} from "@/shared/components/layout/ColocaNavSecondary";
import { ColocaNavUser } from "@/shared/components/layout/ColocaNavUser";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/shared/components/primitives/sidebar";

export type ColocaAppSidebarBrand = {
  label: ReactNode;
  to?: string;
  icon?: ReactNode;
};

export type ColocaAppSidebarUser = {
  name: string;
  email: string;
  avatar?: string;
};

export type ColocaAppSidebarOrganization = {
  name: string;
  avatar?: string;
};

export type ColocaAppSidebarLabels = {
  account: string;
  settings: string;
  logout: string;
  organization: string;
};

export type ColocaAppSidebarProps = {
  sections: ColocaNavMainSection[];
  footerItems?: ColocaNavSecondaryItem[];
  /**
   * Which destination is active, resolved by the caller from its own navigation registry.
   *
   * The sidebar deliberately does not work this out from the URL: "most specific item wins" is a decision
   * about the whole registry, and this component only ever sees one section at a time.
   */
  activeTo?: string | null;
  brand: ColocaAppSidebarBrand;
  user: ColocaAppSidebarUser;
  organization?: ColocaAppSidebarOrganization;
  navUserLabels: ColocaAppSidebarLabels;
  onAccount?: () => void;
  onSettings?: () => void;
  onLogout?: () => void;
  installApp?: ComponentProps<typeof ColocaNavUser>["installApp"];
  updateApp?: ComponentProps<typeof ColocaNavUser>["updateApp"];
} & Omit<ComponentProps<typeof Sidebar>, "children">;

export function ColocaAppSidebar({
  sections,
  footerItems,
  activeTo = null,
  brand,
  user,
  organization,
  navUserLabels,
  onAccount,
  onSettings,
  onLogout,
  installApp,
  updateApp,
  variant = "inset",
  ...props
}: ColocaAppSidebarProps) {
  return (
    <Sidebar variant={variant} {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" render={brand.to ? <Link to={brand.to} /> : <div />}>
              {brand.icon ? (
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  {brand.icon}
                </div>
              ) : null}
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-bold">{brand.label}</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="pb-4">
        <ColocaNavMain sections={sections} activeTo={activeTo} />
        {footerItems && footerItems.length > 0 ? (
          <ColocaNavSecondary items={footerItems} activeTo={activeTo} className="mt-auto" />
        ) : null}
      </SidebarContent>
      <SidebarFooter className="relative border-t border-dashed">
        <div className="absolute -top-[calc(--spacing(10)+1px)] left-0 w-full h-10 pointer-events-none bg-linear-to-t from-sidebar to-transparent"></div>
        <ColocaNavUser
          user={user}
          organization={organization}
          accountLabel={navUserLabels.account}
          settingsLabel={navUserLabels.settings}
          logoutLabel={navUserLabels.logout}
          organizationLabel={navUserLabels.organization}
          onAccount={onAccount}
          onSettings={onSettings}
          onLogout={onLogout}
          installApp={installApp}
          updateApp={updateApp}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
